import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Select, Switch } from "antd";
import { addLecture, editLecture, getLectures } from "../../redux/services/lectureSlice";
import { getCourses } from "../../redux/services/courseSlice";
import { getModules } from "../../redux/services/moduleSlice";
import { getWorkshops } from "../../redux/services/workshopSlice";
import { fileUpload } from "../../redux/services/fileUploadSlice";
import { ROUTES } from "../../constant";
import { AiFillFilePdf } from "react-icons/ai";

const LectureForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const lectureId = queryParams.get("lectureId");
  const moduleId = queryParams.get("moduleId");
  const isEdit = !!lectureId;
  const lectureData = location.state?.row || {};

  const [formData, setFormData] = useState({
    courseIds: [],
    moduleIds: moduleId ? [moduleId] : [],
    workshopIds: [],
    language: "",
    subjectName: "",
    title: "",
    priority: "",
    type: "course",
    subtitle: "",
    image: "",
    link: "",
    pdf: "",
    isLocked: false, // 👈 default value
  });
  const [preview, setPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null);


  const { courseData } = useSelector((state) => state.course);
  const { moduleData } = useSelector((state) => state.module);


  const validate = () => {
    const newErrors = {};
    const required = ["language", "subjectName", "title", "priority", "subtitle", "image", "link"];
    required.forEach((f) => {
      if (!formData[f]) newErrors[f] = "Required";
    });

    if (formData.courseIds.length === 0) newErrors.courseIds = "Select at least one course";


    if (formData.moduleIds.length === 0) newErrors.moduleIds = "Select at least one module";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      ...formData,
      priority: Number(formData.priority),
    };

    if (payload.courseIds.length === 0) delete payload.courseIds;
    if (payload.workshopIds.length === 0) delete payload.workshopIds;

    try {
      const res = isEdit ? await dispatch(editLecture({ ...payload, lectureId })) : await dispatch(addLecture(payload));

      if (res?.payload?.message) toast.success(res.payload.message);
      navigate(`${ROUTES.COURSES.LECTURES}`);
      dispatch(getLectures(formData.moduleIds[0]));
    } catch {
      toast.error("Something went wrong");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSwitchChange = (checked) => {
    setFormData((prev) => ({ ...prev, isLocked: checked }));
  };

  const handleFileUpload = async (file, fileType) => {
    const formData = new FormData();
    if (fileType === "image") {
      formData.append("image", file);
    } else if (fileType === "pdf") {
      formData.append("pdf", file);
    }
    try {
      setUploading(true);
      const res = await dispatch(fileUpload(formData));
      const url = res.payload;
      if (url) {
        if (fileType === "image") {
          setFormData((prev) => ({ ...prev, image: url }));
        } else if (fileType === "pdf") {
          setFormData((prev) => ({ ...prev, pdf: url }));
        }
        setUploading(false);
        toast.success(`${fileType} uploaded successfully!`);
      }
    } catch {
      toast.error(`${fileType} upload failed`);
    } finally {
      setUploading(false);
    }
  }

  const handlePDFChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileURL = URL.createObjectURL(file);
      setPdfPreview(fileURL);
      handleFileUpload(file, "pdf");
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageURL = URL.createObjectURL(file);
      setPreview(imageURL);
      handleFileUpload(file, "image");
    }
  };


  useEffect(() => {
    dispatch(getCourses({ page: 1, limit: 100 }));
    dispatch(getModules({ page: 1, limit: 100 }));
    dispatch(getWorkshops({ page: 1, limit: 100 }));
  }, [dispatch]);

  useEffect(() => {
    console.log(lectureData)
    if (isEdit && lectureData) {
      const mappedCourseIds = lectureData.courseIds?.map((c) => c._id || c) || [];
      const mappedModuleIds = lectureData.moduleIds?.map((m) => m._id || m) || [];
      const mappedWorkshopIds = lectureData.workshopIds?.map((w) => w._id || w) || [];

      setFormData({
        courseIds: mappedCourseIds,
        moduleIds: mappedModuleIds,
        workshopIds: mappedWorkshopIds,
        language: lectureData.language || "",
        subjectName: lectureData.subjectName || "",
        title: lectureData.title || "",
        priority: lectureData.priority || "",
        type: "course",
        subtitle: lectureData.subtitle || "",
        image: lectureData.image || "",
        link: lectureData.link || "",
        pdf: lectureData.pdf || "",
        isLocked: lectureData.isLocked ?? false,
      });

      setPreview(lectureData.image || null);
      setPdfPreview(lectureData.pdf || null);

    }
  }, [isEdit, lectureData]);



  return (
    <div className="max-w-3xl mx-auto bg-card  shadow-theme rounded-xl p-8 mt-6">
      <h2 className="text-2xl font-semibold mb-6">{isEdit ? "Edit Lecture" : "Add Lecture"}</h2>

      <form onSubmit={handleSubmit} className="space-y-5 antForm">


        {/* Course Select */}
        <div>
          <label className="block font-medium mb-1">Select Courses *</label>
          <Select
            mode="multiple"
            value={formData.courseIds}
            onChange={(v) => handleSelectChange("courseIds", v)}
            options={courseData?.course_data?.map((c) => ({
              value: c._id,
              label: c.title,
            }))}
            placeholder="Select course(s)"
            style={{ width: "100%" }}
          />
          {errors.courseIds && <p className="text-red-500 text-sm">{errors.courseIds}</p>}
        </div>

        {/* Module Select */}
        <div>
          <label className="block font-medium mb-1">Select Module *</label>
          <Select
            mode="multiple"
            value={formData.moduleIds}
            onChange={(v) => handleSelectChange("moduleIds", v)}
            options={moduleData?.module_data?.map((m) => ({
              value: m._id,
              label: m.name,
            }))}
            placeholder="Select module(s)"
            style={{ width: "100%" }}
          />
          {errors.moduleIds && <p className="text-red-500 text-sm">{errors.moduleIds}</p>}
        </div>

        {/* Image Upload */}
        <div>
          <label className="block font-medium mb-1">Image *</label>
          <input type="file" onChange={handleImageChange} className="w-full border p-2 rounded-xl" />
          {errors.image && <p className="text-red-500 text-sm">{errors.image}</p>}
          {preview && (
            <div className="mt-3">
              <img src={preview} alt="preview" className="w-24 h-24 object-cover rounded-xl border" />
            </div>
          )}
        </div>

        {/* Text Inputs */}
        {[
          { name: "language", label: "Language" },
          { name: "subjectName", label: "Subject Name" },
          { name: "title", label: "Title" },
          { name: "subtitle", label: "Subtitle" },
          { name: "priority", label: "Priority" },
          { name: "link", label: "Lecture Link" },
        ].map((f) => (
          <div key={f.name}>
            <label className="block font-medium mb-1">{f.label} *</label>
            <input type="text" name={f.name} value={formData[f.name]} onChange={handleChange} className="w-full border p-2 rounded-xl" />
            {errors[f.name] && <p className="text-red-500 text-sm">{errors[f.name]}</p>}
          </div>
        ))}

        {/* PDF Upload */}
        <div>
          <label className="text-sm font-medium text-foreground">PDF</label>
          <input type="file" name="pdf" accept=".pdf" onChange={handlePDFChange} className="w-full px-4 py-2 border border-border rounded-xl" />
          {pdfPreview && (
            <div className="mt-4 flex items-center">
              <p className="text-md text-foreground mr-3">Preview:</p>
              <Link to={pdfPreview} target="_blank" rel="noopener noreferrer" className="flex items-center text-primary">
                <AiFillFilePdf size={40} className="mr-2" />
                View PDF
              </Link>
            </div>
          )}
        </div>

        {/* Lock Switch */}
        <div className="flex items-center gap-3">
          <label className="block font-medium mb-1">Lock Lecture</label>
          <Switch checked={formData.isLocked} onChange={handleSwitchChange} />
          <span className="text-sm text-muted-foreground">{formData.isLocked ? "Locked" : "Unlocked"}</span>
        </div>
        <button type="submit" className="bg-primary text-white px-6 py-2 rounded-xl transition" disabled={uploading}>
          {isEdit ? "Update Lecture" : uploading ? "Uploading..." : "Add Lecture"}
        </button>
      </form>
    </div>
  );
};

export default LectureForm;
